import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Eye, EyeOff, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { api } from '../../lib/api'
import { documentPdfUrl } from '../../lib/documentPdfUrl'
import { useToastStore } from '../../store/toastStore'
import { useAuthStore } from '../../store/authStore'
import { translateApiError } from '../../lib/apiErrors'
import DocumentAcceptance, { DocumentAcceptanceData } from '../../components/auth/DocumentAcceptance'
import MarketingConsent, { MarketingConsentState } from '../../components/marketing/MarketingConsent'
import PhoneInput from '../../components/ui/PhoneInput'
import ReferralCodeInput, { getStoredReferralCode, setStoredReferralCode } from '../../components/referrals/ReferralCodeInput'
import HeardAboutField from '../../components/auth/HeardAboutField'
import { HEARD_ABOUT_OTHER } from '../../lib/heardAbout'
import { AttributionTrackingService } from '../../services/attribution'
import Tooltip from '../../components/ui/Tooltip'
import AuthBrand from '../../components/auth/AuthBrand'
import AuthShell from '../../components/auth/AuthShell'
import {
  authFootnoteClass,
  authHeadingClass,
  authInputClass,
  authLabelClass,
  authSectionDividerClass,
  authSubmitClass,
  authSubtextClass,
} from '../../components/auth/authTheme'


interface PendingRegistration {
  email: string
  tier_code: string
  plan_name: string
  plan_kind: string
}

export default function RegisterAfterPayment() {
  const [searchParams] = useSearchParams()
  const [registrationInfo, setRegistrationInfo] = useState<PendingRegistration | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [acceptedDocuments, setAcceptedDocuments] = useState<DocumentAcceptanceData[]>([])
  const [allDocumentsAccepted, setAllDocumentsAccepted] = useState(false)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [referralCode, setReferralCode] = useState(getStoredReferralCode)
  const [heardAbout, setHeardAbout] = useState('')
  const [heardAboutDetail, setHeardAboutDetail] = useState('')
  const [marketingConsents, setMarketingConsents] = useState<MarketingConsentState>({
    email_marketing: false,
    phone_marketing: false,
  })

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    password: '',
    confirm_password: '',
  })

  const navigate = useNavigate()
  const { t, i18n } = useTranslation(['auth', 'common'])
  const { addToast } = useToastStore()
  const { login } = useAuthStore()

  const token = searchParams.get('token')
  const email = searchParams.get('email')

  useEffect(() => {
    if (!token || !email) {
      addToast(t('auth:registerAfterPayment.invalidLink'), 'error')
      navigate('/')
      return
    }

    const fetchRegistrationInfo = async () => {
      try {
        const response = await api.get(`/credits/pending-registration/${token}`)
        setRegistrationInfo(response.data)
      } catch (error) {
        console.error('Error fetching registration info:', error)
        addToast(t('auth:registerAfterPayment.expiredLink'), 'error')
        navigate('/')
      } finally {
        setLoading(false)
      }
    }

    fetchRegistrationInfo()
  }, [token, email, addToast, navigate, t])

  const handleDocumentAcceptanceChange = (acceptances: DocumentAcceptanceData[], allAccepted: boolean) => {
    setAcceptedDocuments(acceptances)
    setAllDocumentsAccepted(allAccepted)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (formData.password !== formData.confirm_password) {
      addToast(t('common:form.passwordsMismatch'), 'error')
      return
    }

    if (formData.password.length < 8) {
      addToast(t('common:form.passwordTooShort'), 'error')
      return
    }

    if (!formData.first_name || !formData.last_name) {
      addToast(t('common:form.allFieldsRequired'), 'error')
      return
    }

    if (!heardAbout) {
      addToast(t('auth:register.validation.heardAboutRequired'), 'error')
      return
    }

    if (heardAbout === HEARD_ABOUT_OTHER && !heardAboutDetail.trim()) {
      addToast(t('auth:register.validation.heardAboutOtherRequired'), 'error')
      return
    }

    if (!allDocumentsAccepted) {
      addToast(t('common:form.documentsRequired'), 'error')
      return
    }

    setSubmitting(true)

    try {
      await api.post('/credits/complete-registration', {
        email,
        first_name: formData.first_name,
        last_name: formData.last_name,
        password: formData.password,
        confirm_password: formData.confirm_password,
        registration_token: token,
        phone_number: phoneNumber || undefined,
        accepted_documents: acceptedDocuments,
        marketing_consents: marketingConsents,
        referral_code: referralCode.trim() || undefined,
        attribution: AttributionTrackingService.getAttribution() ?? undefined,
        heard_about: heardAbout,
        heard_about_detail: heardAbout === HEARD_ABOUT_OTHER ? heardAboutDetail.trim() : undefined,
      })

      setStoredReferralCode('')

      addToast(t('auth:registerAfterPayment.success'), 'success')

      try {
        await login(email!, formData.password)
        navigate('/dashboard')
      } catch {
        navigate('/auth/login')
      }
    } catch (error) {
      console.error('Registration error:', error)
      addToast(translateApiError(error, t), 'error')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <AuthShell>
        <div className="flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#FF5A00]" />
        </div>
      </AuthShell>
    )
  }

  if (!registrationInfo) {
    return null
  }

  return (
    <AuthShell wide>
      <div>
        <AuthBrand />
        <h2 className={authHeadingClass}>{t('auth:registerAfterPayment.heading')}</h2>
        <p className={authSubtextClass}>{t('auth:registerAfterPayment.description', { email })}</p>
        <p className="mt-3 text-sm text-[#FF5A00] bg-[#1a1a1a] border border-[#FF5A00]/40 rounded-lg px-3 py-2 text-center">
          {t('auth:registerAfterPayment.planLabel', { planName: registrationInfo.plan_name })}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="mt-8 space-y-4">
        <div>
          <label htmlFor="email" className={authLabelClass}>
            {t('auth:registerAfterPayment.emailLabel')}
          </label>
          <input
            type="email"
            id="email"
            value={email || ''}
            disabled
            className={`mt-1 rounded-md ${authInputClass}`}
          />
        </div>

        <div>
          <label htmlFor="first_name" className={authLabelClass}>
            {t('common:form.firstNameLabel')}
          </label>
          <input
            type="text"
            id="first_name"
            value={formData.first_name}
            onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
            className={`mt-1 rounded-md ${authInputClass}`}
            required
            disabled={submitting}
          />
        </div>

        <div>
          <label htmlFor="last_name" className={authLabelClass}>
            {t('common:form.lastNameLabel')}
          </label>
          <input
            type="text"
            id="last_name"
            value={formData.last_name}
            onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
            className={`mt-1 rounded-md ${authInputClass}`}
            required
            disabled={submitting}
          />
        </div>

        <div>
          <div className="flex items-center gap-1.5 mb-1">
            <label className={authLabelClass}>{t('common:form.phoneLabel')}</label>
            <Tooltip content={t('common:form.phoneTooltip')} position="right" multiline>
              <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-600 text-gray-300 text-xs cursor-default select-none leading-none">
                ?
              </span>
            </Tooltip>
            <span className="text-xs text-gray-500 ml-1">{t('common:form.optional')}</span>
          </div>
          <PhoneInput
            value={phoneNumber}
            onChange={setPhoneNumber}
            disabled={submitting}
            variant="dark"
          />
        </div>

        <ReferralCodeInput
          value={referralCode}
          onChange={setReferralCode}
          disabled={submitting}
          variant="dark"
        />

        <HeardAboutField
          value={heardAbout}
          detail={heardAboutDetail}
          onChange={setHeardAbout}
          onDetailChange={setHeardAboutDetail}
          disabled={submitting}
          label={t('auth:register.heardAbout.label')}
          placeholder={t('auth:register.heardAbout.placeholder')}
          detailPlaceholder={t('auth:register.heardAbout.otherPlaceholder')}
          optionLabel={(option) => t(`auth:register.heardAbout.options.${option}`)}
        />

        <div>
          <label htmlFor="password" className={authLabelClass}>
            {t('common:form.passwordLabel')}
          </label>
          <div className="relative mt-1">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className={`rounded-md pr-10 ${authInputClass}`}
              required
              minLength={8}
              disabled={submitting}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-200"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1">{t('auth:registerAfterPayment.passwordMinHint')}</p>
        </div>

        <div>
          <label htmlFor="confirm_password" className={authLabelClass}>
            {t('common:form.confirmPasswordLabel')}
          </label>
          <div className="relative mt-1">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirm_password"
              value={formData.confirm_password}
              onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
              className={`rounded-md pr-10 ${authInputClass}`}
              required
              disabled={submitting}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword((v) => !v)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-200"
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
            >
              {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <div className={authSectionDividerClass}>
          <DocumentAcceptance
            onChange={handleDocumentAcceptanceChange}
            disabled={submitting}
            variant="dark"
          />
        </div>

        <div className={authSectionDividerClass}>
          <MarketingConsent
            value={marketingConsents}
            onChange={setMarketingConsents}
            disabled={submitting}
            variant="dark"
          />
        </div>

        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
          <a
            href={documentPdfUrl('polityka_prywatnosci', i18n.language)}
            target="_blank"
            rel="noopener noreferrer"
            className={authFootnoteClass}
          >
            {t('auth:marketingConsent.informationalDocs.privacyPolicy')}
          </a>
          <a
            href={documentPdfUrl('odstapienie_formularz', i18n.language)}
            target="_blank"
            rel="noopener noreferrer"
            className={authFootnoteClass}
          >
            {t('auth:marketingConsent.informationalDocs.withdrawalForm')}
          </a>
        </div>

        <button
          type="submit"
          disabled={submitting || !allDocumentsAccepted}
          className={authSubmitClass}
        >
          {submitting ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              {t('auth:registerAfterPayment.submitting')}
            </>
          ) : (
            t('auth:registerAfterPayment.submitButton')
          )}
        </button>
      </form>
    </AuthShell>
  )
}
